import { IonGrid } from '@ionic/react'
import { FC, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'

import { GuideClientContent, GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { RootStoreState, CollectionItem, loadCollectionDetails, loadScreen } from 'mytours-read-common'
import { ImageSizePreset, ImageSizePresetMap } from 'common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import '../styles.less'
import CardImage from '../../common/components/card-image'
import Page from '../../common/components/Page'
import { onLinkAction } from '../../screen/functions'
import strings from '../../../strings'

interface Props {
	isForScreens: boolean
}

const GuideClientContents: FC<Props> = ({ isForScreens }) => {
	const dispatch = useDispatch()
	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const selectedLanguage = useSelector((state: RootStoreState) => state.settings.selectedLanguage)
	const screens = useSelector((state: RootStoreState) => state.screen.screens)
	const collections = useSelector((state: RootStoreState) => state.collectionlist.collectionDetails)

	const contents =
		appMetaData?.guide_client_contents?.filter(content => {
			if (isForScreens) {
				return content.content_type === GuideClientContent.ContentTypeEnum.Screen
			} else {
				return content.content_type === GuideClientContent.ContentTypeEnum.Collection
			}
		}) || []

	useEffect(() => {
		contents.forEach(content => {
			if (isForScreens && !screens.get(content.content_id)?.screen) {
				dispatch(
					loadScreen({
						id: content.content_id,
						version: content.version,
						language: selectedLanguage.code,
						accountId: content.account_id,
					}),
				)
			} else if (!isForScreens && !collections.get(content.content_id)?.details) {
				dispatch(
					loadCollectionDetails({
						id: content.content_id,
						version: content.version,
						language: selectedLanguage.code,
						accountId: content.account_id,
					}),
				)
			}
		})
	}, [appMetaData])

	const title = isForScreens ? strings.screens : strings.collections

	const items = contents.map(content => {
		const item = isForScreens ? screens.get(content.content_id)?.screen : collections.get(content.content_id)?.details

		return {
			item_id: content.content_id,
			item_type:
				content.content_type === GuideClientContent.ContentTypeEnum.Screen
					? GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
					: GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection,
			item_subtype: content.subtype as unknown as GetCollectionItemsIndexResponseData.ItemSubtypeEnum,
			title: item?.title || content.short_title?.[appMetaData.primary_language] || '',
			cover_image: item?.cover_image,
			cover_image_grid: item?.cover_image_grid,
		}
	})

	const getImageSize = () => {
		const imageSizePreset = ImageSizePreset.Row
		return {
			imageSizePreset,
			...ImageSizePresetMap[imageSizePreset],
		}
	}

	const onItemClicked = (item: CollectionItem) => () => {
		onLinkAction(`${isForScreens ? 'screen' : 'collection'}_${item.item_subtype}`, item.item_id, item.version, () => {})
	}

	const renderItem = (item: CollectionItem, index: number) => {
		const size = getImageSize()

		const coverImageGridFocalPoint = item?.cover_image_grid?.file?.focal_point
		const coverImageFocalPoint = item?.cover_image?.file?.focal_point
		const coverImageGridCrop =
			coverImageGridFocalPoint && (coverImageGridFocalPoint[0] !== 0.5 || coverImageGridFocalPoint[1] !== 0.5)
		const coverImageCrop = coverImageFocalPoint && (coverImageFocalPoint[0] !== 0.5 || coverImageFocalPoint[1] !== 0.5)

		const coverImageGridOptions = {
			crop: coverImageGridCrop,
			focalPoint: coverImageGridFocalPoint,
		}

		const coverImageOptions = {
			crop: coverImageCrop,
			focalPoint: coverImageFocalPoint,
		}

		const coverImageGrid =
			item?.cover_image_grid_url_display ||
			(item?.cover_image_grid?.file &&
				generateImageUrl(item?.cover_image_grid.file, {
					...size,
					...coverImageGridOptions,
				}))

		const coverImageGrid2x =
			item?.cover_image_grid?.file &&
			generateImageUrl(item?.cover_image_grid.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageGridOptions,
			})

		const coverImage =
			item?.cover_image_url_display ||
			(item?.cover_image?.file &&
				generateImageUrl(item?.cover_image.file, {
					...size,
					...coverImageOptions,
				}))

		const coverImage2x =
			item?.cover_image?.file &&
			generateImageUrl(item?.cover_image.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageOptions,
			})

		return (
			<CardImage
				key={index}
				loading={false}
				className="collection-list-item list-card-image"
				image={coverImageGrid ? coverImageGrid : coverImage}
				image2x={coverImageGrid2x ? coverImageGrid2x : coverImage2x}
				label={item.title}
				role="button"
				onClick={onItemClicked(item)}
				itemId={item.item_id}
				itemType={item.item_type}
				item={item}
				appMetaData={appMetaData}
				toggleFavorite={() => {}}
				isFavourited={false}
			/>
		)
	}

	return (
		<>
			<Helmet
				title={title}
				meta={[
					{ name: 'description', content: title },
					{ property: 'og:type', content: 'website' },
					{ property: 'og:title', content: title },
					{ property: 'og:description', content: title },
					{ property: 'og:url', content: window.location.href },
				]}
			/>
			<Page
				contentProps={{
					className: 'guide-client-contents-content',
				}}
				headerProps={{
					type: 'short',
					title: '‎',
					hideShareButton: true,
					hideFavouriteButton: true,
					collectionItem: null,
					itemType: null,
					itemId: null,
					collection: null,
					screen: null,
				}}
				footer={{
					component: <AudioPlayerBarComponent bottomOffset={-1} />,
				}}
				dataCy="page-guide-client-contents"
			>
				<IonGrid className="list-container">
					<div className="list-items grid">
						{items.map((item: CollectionItem, index: number) => renderItem(item, index))}
					</div>
				</IonGrid>
			</Page>
		</>
	)
}

export default GuideClientContents
